import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Send, Lock, ArrowLeft, MessagesSquare, BadgeCheck, ImagePlus, Sparkles, Gift } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import CheckoutModal from "@/components/CheckoutModal";
import GiftAnimation from "@/components/GiftAnimation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

export default function Messages() {
  const { user } = useAuth();
  const isCreator = user?.role === "creator";
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState(null);
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [ppvMode, setPpvMode] = useState(false);
  const [ppvPrice, setPpvPrice] = useState("9.99");
  const [sending, setSending] = useState(false);
  const [unlockMsg, setUnlockMsg] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [showGifts, setShowGifts] = useState(false);
  const [sendingGift, setSendingGift] = useState(false);
  const [giftFx, setGiftFx] = useState(null);
  const seenGifts = useRef(new Set());
  const giftInit = useRef(null);
  const bottomRef = useRef(null);

  const triggerGiftFx = (emoji) => {
    setGiftFx({ emoji, key: Date.now() });
    setTimeout(() => setGiftFx(null), 3000);
  };

  useEffect(() => { api.get("/gifts").then(({ data }) => setGifts(data)).catch(() => setGifts([])); }, []);

  // reset gift-seen tracking when switching conversations
  useEffect(() => { seenGifts.current = new Set(); giftInit.current = null; }, [activeId]);

  // detect newly arrived gifts (from either side) via polling and animate
  useEffect(() => {
    if (!thread?.messages || !activeId) return;
    const giftMsgs = thread.messages.filter((m) => m.kind === "gift");
    if (giftInit.current !== activeId) {
      giftMsgs.forEach((m) => seenGifts.current.add(m.id));
      giftInit.current = activeId;
      return;
    }
    const fresh = giftMsgs.filter((m) => !seenGifts.current.has(m.id));
    fresh.forEach((m) => seenGifts.current.add(m.id));
    if (fresh.length) triggerGiftFx(fresh[fresh.length - 1].gift_emoji);
  }, [thread, activeId]);

  const sendGift = async (giftId) => {
    if (sendingGift) return;
    setSendingGift(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/gift`, { gift_id: giftId });
      if (data?.id) seenGifts.current.add(data.id);
      triggerGiftFx(data?.gift_emoji);
      setShowGifts(false);
      loadThread(activeId);
      loadConversations();
      toast.success("Regalo inviato! 🎁");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio regalo fallito");
    } finally { setSendingGift(false); }
  };

  const loadConversations = useCallback(() => {
    api.get("/conversations").then(({ data }) => setConversations(data)).catch(() => setConversations([]));
  }, []);

  const loadThread = useCallback((id) => {
    if (!id) return;
    api.get(`/conversations/${id}/messages`).then(({ data }) => setThread(data)).catch(() => setThread(null));
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => {
    const t = setInterval(loadConversations, 8000);
    return () => clearInterval(t);
  }, [loadConversations]);

  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    loadThread(activeId);
    const t = setInterval(() => loadThread(activeId), 4000);
    return () => clearInterval(t);
  }, [activeId, loadThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const openConv = (id) => { setActiveId(id); setParams({ c: id }); };
  const backToList = () => { setActiveId(null); setParams({}); setThread(null); };

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const body = { text: text.trim() };
    if (isCreator && ppvMode) { body.is_locked = true; body.price = parseFloat(ppvPrice) || 0; }
    try {
      await api.post(`/conversations/${activeId}/messages`, body);
      setText(""); setPpvMode(false);
      loadThread(activeId);
      loadConversations();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio fallito");
    } finally { setSending(false); }
  };

  const confirmUnlock = async () => {
    try {
      await api.post(`/messages/${unlockMsg.id}/unlock`);
      toast.success("Contenuto sbloccato!");
      loadThread(activeId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
      throw e;
    }
  };

  const peer = thread?.peer;

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen text-white">
      <AppNav />
      {giftFx && <GiftAnimation emoji={giftFx.emoji} fxKey={giftFx.key} />}
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden pb-16 md:pb-0" style={{ height: "100vh" }}>
        {/* Conversation list */}
        <aside className={`w-full md:w-80 md:border-r border-white/10 flex-col ${activeId ? "hidden md:flex" : "flex"}`}>
          <div className="p-5 border-b border-white/10">
            <h1 className="text-2xl font-black font-heading flex items-center gap-2"><MessagesSquare className="w-6 h-6 text-[#E91E8C]" /> Messaggi</h1>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations === null ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#E91E8C] animate-spin" /></div>
            ) : conversations.length === 0 ? (
              <div className="text-center text-white/40 text-sm p-8" data-testid="conversations-empty">
                {isCreator ? "Nessun messaggio ancora. I tuoi fan possono scriverti dal tuo profilo." : "Nessuna conversazione. Apri il profilo di un creator e invia un messaggio."}
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id} onClick={() => openConv(c.id)} data-testid={`conversation-${c.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 border-b border-white/5 text-left hover:bg-white/5 transition-colors ${activeId === c.id ? "bg-white/5" : ""}`}
                >
                  {c.peer_avatar ? (
                    <img src={mediaUrl(c.peer_avatar)} alt={c.peer_name} className="w-11 h-11 rounded-full object-cover border border-white/10 shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#E91E8C]/15 flex items-center justify-center font-bold shrink-0">{(c.peer_name || "?")[0].toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{c.peer_name}</p>
                    <p className="text-xs text-white/40 truncate">{c.last_message || "Nessun messaggio"}</p>
                  </div>
                  {c.unread > 0 && <span className="bg-[#E91E8C] text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center shrink-0" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Chat thread */}
        <section className={`flex-1 flex-col ${activeId ? "flex" : "hidden md:flex"}`}>
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center text-white/30 text-sm">Seleziona una conversazione</div>
          ) : !thread ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-7 h-7 text-[#E91E8C] animate-spin" /></div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-white/10">
                <button onClick={backToList} className="md:hidden p-1" data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></button>
                {peer?.avatar ? (
                  <img src={mediaUrl(peer.avatar)} alt={peer.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#E91E8C]/15 flex items-center justify-center font-bold">{(peer?.name || "?")[0].toUpperCase()}</div>
                )}
                <div>
                  <p className="font-semibold text-sm flex items-center gap-1.5">{peer?.name} {peer?.peer_role === "creator" && <BadgeCheck className="w-4 h-4 text-[#F5C6D6]" />}</p>
                  <p className="text-xs text-white/40 capitalize">{peer?.peer_role}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-thread">
                {thread.messages.map((m) => {
                  const mine = m.sender_id === thread.me_id;
                  if (m.kind === "gift") {
                    return (
                      <div key={m.id} className="flex justify-center" data-testid={`gift-message-${m.id}`}>
                        <div className="text-center bg-gradient-to-br from-[#E91E8C]/20 to-[#1A1A1A] border border-[#E91E8C]/40 rounded-2xl px-5 py-3">
                          <div className="text-3xl mb-1">{m.gift_emoji}</div>
                          <p className="text-xs text-white/80"><span className="font-semibold">{mine ? "Tu" : m.sender_name}</span> ha inviato {m.gift_name}</p>
                          <p className="text-[10px] text-white/30 mt-0.5">{fmtTime(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`message-${m.id}`} className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#E91E8C] text-white" : "bg-[#1A1A1A] border border-white/10"}`}>
                        {m.is_locked && !m.unlocked ? (
                          <div className="text-center py-2" data-testid={`locked-message-${m.id}`}>
                            <Lock className="w-5 h-5 mx-auto mb-1.5 text-[#F5C6D6]" />
                            <p className="text-xs text-white/70 mb-2">Contenuto esclusivo</p>
                            <Button size="sm" onClick={() => setUnlockMsg(m)} data-testid={`unlock-message-${m.id}`} className="rounded-full bg-white text-[#0D0D0D] hover:bg-white/90 text-xs h-8">
                              <Lock className="w-3 h-3 mr-1" /> Sblocca €{Number(m.price).toFixed(2)}
                            </Button>
                          </div>
                        ) : (
                          <>
                            {m.is_locked && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-[#F5C6D6] mb-1"><Sparkles className="w-3 h-3" /> Contenuto sbloccato</span>}
                            {m.media_url && <img src={mediaUrl(m.media_url)} alt="" className="rounded-xl mb-1.5 max-h-60 object-cover" />}
                            {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                          </>
                        )}
                        <p className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-white/30"}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-white/10 p-3">
                {isCreator && (
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <button
                      onClick={() => setPpvMode(!ppvMode)} data-testid="ppv-toggle"
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${ppvMode ? "bg-[#E91E8C] border-[#E91E8C] text-white" : "border-white/15 text-white/70 hover:border-[#E91E8C]/50"}`}
                    >
                      <Lock className="w-3 h-3" /> Contenuto a pagamento
                    </button>
                    {ppvMode && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-white/50">Prezzo €</span>
                        <Input value={ppvPrice} onChange={(e) => setPpvPrice(e.target.value)} data-testid="ppv-price" type="number" min="0.5" step="0.5" className="w-20 h-8 bg-[#0D0D0D] border-white/10 text-sm" />
                      </div>
                    )}
                  </div>
                )}
                {!isCreator && showGifts && (
                  <div className="flex gap-2 mb-2 overflow-x-auto no-scrollbar pb-1" data-testid="gift-picker">
                    {gifts.map((g) => (
                      <button
                        key={g.id} onClick={() => sendGift(g.id)} disabled={sendingGift} data-testid={`gift-${g.id}`}
                        className="flex flex-col items-center gap-0.5 bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2 shrink-0 hover:border-[#E91E8C]/50 transition-colors disabled:opacity-50"
                      >
                        <span className="text-2xl">{g.emoji}</span>
                        <span className="text-[10px] text-white/60 whitespace-nowrap">{g.name}</span>
                        <span className="text-[10px] font-bold text-[#E91E8C]">€{g.price.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {!isCreator && (
                    <Button
                      onClick={() => setShowGifts(!showGifts)} data-testid="gift-toggle" variant="outline"
                      className={`rounded-full w-11 h-11 p-0 shrink-0 border-white/15 ${showGifts ? "bg-[#E91E8C] text-white border-[#E91E8C]" : "bg-transparent text-[#F5C6D6] hover:bg-white/5"}`}
                    >
                      <Gift className="w-4 h-4" />
                    </Button>
                  )}
                  <Input
                    value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={ppvMode ? "Descrivi il contenuto esclusivo..." : "Scrivi un messaggio..."}
                    className="flex-1 bg-[#1A1A1A] border-white/10 rounded-full"
                  />
                  <Button onClick={send} disabled={sending || !text.trim()} data-testid="send-message-btn" className="rounded-full bg-[#E91E8C] hover:bg-[#D8157B] w-11 h-11 p-0 shrink-0">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      </main>

      <CheckoutModal
        open={!!unlockMsg} onOpenChange={(o) => { if (!o) setUnlockMsg(null); }}
        title="Sblocca contenuto" subtitle={peer ? `Contenuto di ${peer.name}` : ""}
        lines={unlockMsg ? [{ label: "Tipo", value: "Contenuto in chat" }] : []}
        total={unlockMsg?.price || 0} confirmLabel="Simula pagamento riuscito" onConfirm={confirmUnlock}
      />
    </div>
  );
}
